import React from "react";
import Button from "@mui/material/Button";
import { Modal, Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form } from "react-final-form";
import * as Yup from "yup";
import { TextField, Select, Autocomplete, makeValidate } from "mui-rff";
import MenuItem from "@mui/material/MenuItem";
import styles from "./CreateConsentTemplateForm.module.scss";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";

const schema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  category_id: Yup.string().required("Category is required"),
  treatment_ids: Yup.array().min(1, "At least one treatment is required"),
});

const validate = makeValidate(schema);

function CreateConsentTemplateForm({
  isOpen,
  close,
  categories,
  treatmentPlans,
  setCurrentTemplate,
}) {
  const queryClient = useQueryClient();

  const createConsentTemplate = useMutation(
    (data) => axios.post(`/consent_clause`, data),
    {
      onSuccess: (data) => {
        try {
          setCurrentTemplate(data.data.id);
          queryClient.invalidateQueries({ queryKey: ["consentClauses"] });
          toast.success("Consent clause created successfully.");
        } catch (error) {
          console.error("Error during onSuccess operations:", error);
        }
        close();
      },
      onError: (error) => {
        if (error?.response?.data?.detail) {
          toast.error(error.response.data.detail);
        } else {
          toast.error("Failed to create consent clause.");
        }
      },
    }
  );

  const handleSubmit = async (data) => {
    const treatment_ids = (data.treatment_ids || []).map((treatment) => treatment.id);
    const treatment_id = treatment_ids[0] || null;

    const payload = {
      ...data,
      treatment_id,
      treatment_ids,
    };

    try {
      await createConsentTemplate.mutateAsync(payload);
      return data.name;
    } catch (error) {
      console.error("Error creating consent template:", error);
      return null;
    }
  };

  return (
    <Modal open={isOpen} onClose={close}>
      <Box className={styles.modalOverlay}>
        <Box className={styles.modalCard}>
          <div className={styles.modalHeader}>
            <div>
              <Typography variant="h5" className={styles.modalTitle}>
                Create New Consent Clause
              </Typography>
              <Typography className={styles.modalSubtitle}>
                Create a new clause and link it to category and treatments.
              </Typography>
            </div>

            <IconButton onClick={close} className={styles.closeButton}>
              <CloseIcon />
            </IconButton>
          </div>

          <Form
            onSubmit={handleSubmit}
            validate={validate}
            render={({ handleSubmit }) => (
              <form
                className={styles.form}
                onSubmit={handleSubmit}
                noValidate
              >
                <div className={styles.fieldGroup}>
                  <TextField
                    label="Name"
                    name="name"
                    required
                    variant="outlined"
                  />
                </div>

                <div className={styles.fieldGroup}>
                  <Select
                    name="category_id"
                    label="Category"
                    required
                    formControlProps={{ margin: "normal", fullWidth: true }}
                  >
                    {categories?.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.category_name}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <div className={styles.fieldGroup}>
                  <Autocomplete
                    name="treatment_ids"
                    label="Treatment"
                    multiple
                    required
                    options={treatmentPlans || []}
                    getOptionLabel={(option) => option.label || ""}
                    getOptionSelected={(option, value) =>
                      option?.value === value?.value
                    }
                  />
                </div>

                <div className={styles.footer}>
                  <Button
                    onClick={close}
                    variant="outlined"
                    className={styles.cancelButton}
                  >
                    Cancel
                  </Button>

                  <Button
                    className={styles.submitButton}
                    type="submit"
                    variant="contained"
                    disabled={createConsentTemplate.isLoading}
                  >
                    {createConsentTemplate.isLoading ? "Creating..." : "Create"}
                  </Button>
                </div>
              </form>
            )}
          />
        </Box>
      </Box>
    </Modal>
  );
}

export default CreateConsentTemplateForm;